import {
  keepPreviousData,
  type QueryClient,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { DownloadTemplateSelection } from '@/entities/download-template';
import { searchKeys } from '@/entities/search';
import { API_TAGS, createLazyQuery, getApiErrorStatus } from '@/shared/api';
import { getShortName } from '@/shared/lib';
import { delay } from '@/shared/lib/utils';
import { downloadFileFromBlob } from '@/shared/lib/utils/file';
import { buildConversationAudioUrl } from './audioUrl';
import {
  applyConversationValues,
  createConversation,
  createConversationComment,
  deleteConversationComment,
  downloadConversationTranscript,
  fetchAllConversationsAttributes,
  fetchConversation,
  fetchConversationAttachmentSignedUrl,
  fetchConversationAudioBlob,
  fetchConversationAudioUrl,
  fetchConversationChecklist,
  fetchConversationCommentAuthors,
  fetchConversationComments,
  fetchConversationsAttributes,
  fetchConversationsClients,
  fetchConversationsEmployees,
  fetchConversationSignedUrl,
  fetchConversationTranscript,
  runSpeechAnalytics,
  updateConversationComment,
  uploadConversationAudio,
} from './api';
import type { CommentCreateRequest, ConversationCreate, ConversationSignedUrlResponse } from './types';

/**
 * Query keys for conversations
 */
export const conversationsKeys = {
  all: [API_TAGS.DIALOGS] as const,
  lists: () => [...conversationsKeys.all, 'list'] as const,
  details: () => [...conversationsKeys.all, 'detail'] as const,
  detail: (id: string | number) => [...conversationsKeys.details(), id] as const,
  transcripts: () => [...conversationsKeys.all, 'transcript'] as const,
  transcript: (id: string | number, anonymized = true) => [...conversationsKeys.transcripts(), id, anonymized] as const,
  audioBlobs: () => [...conversationsKeys.all, 'audio-blob'] as const,
  audioBlob: (id: string | number) => [...conversationsKeys.audioBlobs(), id] as const,
  audioUrls: () => [...conversationsKeys.all, 'audio-url'] as const,
  audioUrl: (id: string | number) => [...conversationsKeys.audioUrls(), id] as const,
  checklists: () => [...conversationsKeys.all, 'checklist'] as const,
  checklist: (id: string | number, checklistId: string | number) =>
    [...conversationsKeys.checklists(), id, checklistId] as const,
  employees: () => [...conversationsKeys.all, 'employees'] as const,
  employeesByIds: (ids: string) => [...conversationsKeys.employees(), 'by-ids', ids] as const,
  clients: () => [...conversationsKeys.all, 'clients'] as const,
  clientsByIds: (ids: string) => [...conversationsKeys.clients(), 'by-ids', ids] as const,
  attributes: () => [...conversationsKeys.all, 'attributes'] as const,
  attributesList: (params?: Parameters<typeof fetchConversationsAttributes>[0]) =>
    [...conversationsKeys.attributes(), { ...params }] as const,
  attributesAll: (params?: Parameters<typeof fetchAllConversationsAttributes>[0]) =>
    [...conversationsKeys.attributes(), 'all', { ...params }] as const,
  comments: () => [...conversationsKeys.all, 'comments'] as const,
  commentsList: (id: string | number) => [...conversationsKeys.comments(), id] as const,
  commentAuthors: () => [...conversationsKeys.all, 'comment-authors'] as const,
  commentAuthorsList: (params?: Parameters<typeof fetchConversationCommentAuthors>[0]) =>
    [...conversationsKeys.commentAuthors(), { ...params }] as const,
  signedUrls: () => [...conversationsKeys.all, 'signed-url'] as const,
  signedUrl: (id: string | number) => [...conversationsKeys.signedUrls(), id] as const,
  attachmentSignedUrls: () => [...conversationsKeys.all, 'attachment-signed-url'] as const,
  attachmentSignedUrl: (attachmentId: string | number) =>
    [...conversationsKeys.attachmentSignedUrls(), attachmentId] as const,
};

/**
 * Hook to fetch a single conversation by ID
 *
 * `retry: 0` + `retryOnMount: false`: при переключении макета на `DialogsPage`
 * активный макет перемонтируется. Без этих флагов запрос несуществующего диалога
 * (404) при каждом перемонтировании уходил бы в повторный fetch.
 *
 * @param id - Conversation ID
 */
export const useConversation = (id?: number | string) => {
  return useQuery({
    queryKey: conversationsKeys.detail(+id!),
    queryFn: ({ signal }) => fetchConversation(id!, { signal }),
    enabled: !!id,
    retry: 0,
    retryOnMount: false,
  });
};

/**
 * Hook to fetch conversation transcript per OpenAPI GET /conversation/{id}/transcript
 * Получение полного транскрипта разговора в виде упорядоченного списка фраз
 * с информацией о говорящих и временных метках.
 *
 * Анонимизированный и неанонимизированный виды кэшируются под разными ключами
 * (`anonymized` входит в ключ), поэтому переключение не затирает уже загруженный вид.
 * @param id - Conversation ID
 * @param anonymized - Вид текста реплик; по умолчанию true (анонимизированный)
 */
export const useConversationTranscript = (id?: string | number, anonymized = true) => {
  return useQuery({
    queryKey: conversationsKeys.transcript(+id!, anonymized),
    queryFn: ({ signal }) => fetchConversationTranscript(id!, anonymized, { signal }),
    enabled: !!id,
  });
};

/**
 * Hook to fetch audio blob for a conversation
 * @param id - Conversation ID
 */
export const useConversationAudioBlob = (id?: string | number) => {
  return useQuery({
    queryKey: conversationsKeys.audioBlob(id!),
    queryFn: () => fetchConversationAudioBlob(id!),
    enabled: !!id,
  });
};

/**
 * Hook to fetch audio URL for a conversation
 * @param id - Conversation ID
 */
export const useConversationAudioUrl = (id?: string | number) => {
  return useQuery({
    queryKey: conversationsKeys.audioUrl(id!),
    queryFn: () => fetchConversationAudioUrl(id!),
    enabled: !!id,
  });
};

/**
 * Hook to upload conversation audio file
 */
export const useUploadConversationAudio = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: uploadConversationAudio,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: conversationsKeys.lists() });
    },
  });
};

/**
 * Hook to create a conversation
 * Новая запись меняет и списки диалогов, и выдачу поиска, из которой строится список диалогов, —
 * обесцениваются оба: иначе перечитанный список до истечения свежести выдачи показывал бы её без записи.
 */
export const useCreateConversation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: ConversationCreate) => createConversation(payload),
    onSuccess: (data) => {
      // Invalidate conversations list and search results
      queryClient.invalidateQueries({ queryKey: conversationsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: searchKeys.all });
      // Set the new conversation in cache
      queryClient.setQueryData(conversationsKeys.detail(data.id), data);
    },
  });
};

/**
 * Проставление отмеченным записям сотрудника, времени звонка и значений атрибутов.
 * Меняет строки списков, выдачу поиска и карточки записей — обесценивает все три.
 */
export const useApplyConversationValues = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: applyConversationValues,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: conversationsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: conversationsKeys.details() });
      queryClient.invalidateQueries({ queryKey: searchKeys.all });
    },
  });
};

/**
 * Hook to fetch conversation checklist
 */
export const useConversationChecklist = (id: string | number, checklistId?: string | number) => {
  return useQuery({
    queryKey: conversationsKeys.checklist(+id, +checklistId!),
    queryFn: () => fetchConversationChecklist(+id, +checklistId!),
    enabled: !!id && !!checklistId,
  });
};

/**
 * Hook to fetch employees of conversations
 */
export const useConversationsEmployees = (params?: Parameters<typeof fetchConversationsEmployees>[0]) => {
  return useQuery({
    queryKey: [...conversationsKeys.employees(), { ...params }] as const,
    queryFn: () => fetchConversationsEmployees(params),
  });
};

/**
 * Hook to fetch conversations' attributes
 */
export const useConversationsAttributes = (params?: Parameters<typeof fetchConversationsAttributes>[0]) => {
  return useQuery({
    queryKey: conversationsKeys.attributesList(params),
    queryFn: () => fetchConversationsAttributes(params),
  });
};

/**
 * Хук всего набора атрибутов для таблицы диалогов: набор доходит до последнего атрибута, сколько бы
 * окон выдачи он ни занимал. Нужен тем, кто предлагает выбрать атрибут из всех, — первое окно
 * выдачи за весь набор им выдавать нельзя.
 */
export const useAllConversationsAttributes = (params?: Parameters<typeof fetchAllConversationsAttributes>[0]) => {
  return useQuery({
    queryKey: conversationsKeys.attributesAll(params),
    queryFn: () => fetchAllConversationsAttributes(params),
  });
};

/** Имена участников диалогов, запрошенные по их идентификаторам */
export type ConversationPersonNames = {
  /** Идентификатор участника → ФИО; участника, которого нет в справочнике, в наборе нет */
  names: Record<number, string>;
  /** Имена ещё запрашиваются */
  isLoading: boolean;
};

/**
 * Набор идентификаторов как параметр точечной выборки: дубли не нужны, порядок не важен.
 * Нечисловые значения отбрасываются — идентификаторы участников числовые, а в набор они
 * попадают в том числе из сохранённых фильтров, где формат значения не гарантирован.
 */
export const toPersonIdsParam = (ids: Array<string | number>): string =>
  [...new Set(ids.map(Number))]
    .filter(Number.isFinite)
    .sort((a, b) => a - b)
    .join(',');

/**
 * Хук имён участников диалогов по их идентификаторам.
 *
 * Нужен там, где известны только идентификаторы участников (например, в фильтрах списка):
 * поиск по имени и первая страница справочника выбранных участников не гарантируют, поэтому
 * имена берутся точечной выборкой по идентификаторам.
 */
export const useConversationPersonNames = ({
  employeeIds,
  clientIds,
}: {
  employeeIds: number[];
  clientIds: number[];
}): ConversationPersonNames => {
  const employeeIdsParam = toPersonIdsParam(employeeIds);
  const clientIdsParam = toPersonIdsParam(clientIds);

  const employeesQuery = useQuery({
    queryKey: conversationsKeys.employeesByIds(employeeIdsParam),
    queryFn: () => fetchConversationsEmployees({ 'employee-id': employeeIdsParam }),
    enabled: Boolean(employeeIdsParam),
  });

  const clientsQuery = useQuery({
    queryKey: conversationsKeys.clientsByIds(clientIdsParam),
    queryFn: () => fetchConversationsClients({ 'client-id': clientIdsParam }),
    enabled: Boolean(clientIdsParam),
  });

  const names = useMemo(() => {
    const result: Record<number, string> = {};
    const persons = [...(employeesQuery.data?.employees ?? []), ...(clientsQuery.data?.clients ?? [])];
    persons.forEach((person) => {
      result[person.id] = getShortName(person.firstName, person.lastName, person.middleName);
    });
    return result;
  }, [employeesQuery.data, clientsQuery.data]);

  return { names, isLoading: employeesQuery.isLoading || clientsQuery.isLoading };
};

/**
 * Хук для ленивой загрузки сотрудников (ручной триггер).
 * Использует кэш React Query — повторный вызов с теми же params не делает HTTP-запрос.
 */
export const useLazyConversationsEmployees = createLazyQuery(
  (params: Parameters<typeof fetchConversationsEmployees>[0]) => [...conversationsKeys.employees(), { ...params }],
  fetchConversationsEmployees,
);

/**
 * Хук для ленивой загрузки клиентов (ручной триггер).
 * Использует кэш React Query — повторный вызов с теми же params не делает HTTP-запрос.
 */
export const useLazyConversationsClients = createLazyQuery(
  (params: Parameters<typeof fetchConversationsClients>[0]) => [...conversationsKeys.clients(), { ...params }],
  fetchConversationsClients,
);

/**
 * Hook to fetch comments for a conversation
 * @param id - Conversation ID
 */
export const useConversationComments = (id?: number | string) => {
  return useQuery({
    queryKey: conversationsKeys.commentsList(id!),
    queryFn: () => fetchConversationComments(id!),
    enabled: !!id,
  });
};

/**
 * Хук для получения авторов комментариев (наполнение выпадающего списка выбора автора
 * в поиске по комментариям). Загружается только когда список реально нужен — вызывать
 * из компонента, монтируемого лишь в активном режиме поиска по комментариям.
 */
export const useConversationCommentAuthors = (params?: Parameters<typeof fetchConversationCommentAuthors>[0]) => {
  return useQuery({
    queryKey: conversationsKeys.commentAuthorsList(params),
    queryFn: () => fetchConversationCommentAuthors(params),
  });
};

/**
 * Hook to fetch comments for a conversation with infinite scroll
 * @param id - Conversation ID
 * @param params - Query parameters (limit, offset)
 */
export const useInfiniteConversationComments = (id?: number | string, params?: { limit?: number; offset?: number }) => {
  const limit = params?.limit || 20;
  const initialPageParam = Math.floor((params?.offset || 0) / limit);

  const query = useInfiniteQuery({
    queryKey: [...conversationsKeys.commentsList(id!), { ...params }] as const,
    queryFn: ({ pageParam }) => fetchConversationComments(id!, { limit, offset: pageParam * limit }),
    initialPageParam,
    placeholderData: keepPreviousData,
    enabled: !!id,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      return lastPage.total <= limit * (lastPageParam + 1) ? undefined : lastPageParam + 1;
    },
  });

  const data = useMemo(
    () => ({
      total: query.data?.pages[0]?.total,
      comments: query.data?.pages.flatMap((p) => p.comments) || [],
    }),
    [query.data],
  );

  return {
    ...query,
    data,
  };
};

/**
 * Обесценивает списки диалогов после правки комментария.
 *
 * Комментарии видны не только в ленте открытого диалога: строка списка перечисляет их,
 * а режим «Поиск по комментариям» отбирает по ним диалоги. Без этого возврат к списку
 * показывал бы выдачу, полученную до правки.
 */
const invalidateConversationLists = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: conversationsKeys.lists() });
  queryClient.invalidateQueries({ queryKey: searchKeys.all });
};

/**
 * Hook to create a comment for a conversation
 */
export const useCreateConversationComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: conversationsKeys.comments(),
    mutationFn: ({ id, payload }: { id: number | string; payload: CommentCreateRequest }) =>
      createConversationComment(id, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: conversationsKeys.commentsList(variables.id) });
      invalidateConversationLists(queryClient);
    },
  });
};

/**
 * Hook to update a comment
 */
export const useUpdateConversationComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: conversationsKeys.comments(),
    mutationFn: ({ id, payload }: { id: number | string; payload: CommentCreateRequest }) =>
      updateConversationComment(id, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: conversationsKeys.commentsList(data.conversationId) });
      invalidateConversationLists(queryClient);
    },
  });
};

/**
 * Hook to delete a comment
 * Удаление комментария. Только автор комментария может его удалить.
 * Требуется разрешение VIEW_CONVERSATION.
 */
export const useDeleteConversationComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: conversationsKeys.comments(),
    mutationFn: (id: string | number): Promise<void> => deleteConversationComment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: conversationsKeys.comments() });
      invalidateConversationLists(queryClient);
    },
  });
};

/**
 * Hook to run speech analytics for a conversation
 */
export const useRunSpeechAnalytics = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number | string) => runSpeechAnalytics(id),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: conversationsKeys.detail(variables) });
    },
  });
};

/**
 * Хук для ленивого получения подписанной ссылки на вложение (ручной триггер).
 * Использует кэш React Query — повторный вызов с тем же attachmentId не делает HTTP-запрос.
 * Применимо только для вложений с isExternalUrl=false.
 */
export const useLazyConversationAttachmentSignedUrl = createLazyQuery(
  (attachmentId: number) => conversationsKeys.attachmentSignedUrl(attachmentId),
  fetchConversationAttachmentSignedUrl,
);

/**
 * Хук для скачивания транскрипции диалога по выбранному шаблону.
 * Скачивает файл и инициирует загрузку через blob URL; расширение имени берётся из выбранного
 * формата — заголовок ответа кросс-доменному запросу браузером не открывается.
 */
export const useDownloadConversationTranscript = () => {
  return useMutation({
    mutationFn: ({ id, templateId, format }: { id: number | string } & DownloadTemplateSelection) =>
      downloadConversationTranscript(id, templateId, format),
    onSuccess: (blob, { id, format }) => {
      downloadFileFromBlob(blob, `transcript_${id}.${format}`);
    },
  });
};

/**
 * Максимальное количество попыток опроса при отсутствии аудио
 */
const AUDIO_POLL_MAX_ATTEMPTS = 10;

/**
 * Интервал опроса при отсутствии аудио (мс)
 */
const AUDIO_POLL_INTERVAL_MS = 1000;

/**
 * Сколько всего ждать запись, которую подгружает интеграция-источник (мс).
 * Ожидание идёт по действию пользователя и держит кнопку в состоянии получения записи,
 * поэтому у него есть предел: не дождавшись, честнее сказать о неудаче, чем ждать молча.
 */
const RECORDING_WAIT_TIMEOUT_MS = 60_000;

/**
 * С какой паузы начинается опрос ссылки, пока запись ожидается от источника (мс).
 */
const RECORDING_WAIT_INITIAL_INTERVAL_MS = 2000;

/**
 * До какой паузы между запросами ссылки доходит опрос ожидания (мс).
 * Пауза удваивается от запроса к запросу: каждый запрос заново заказывает получение записи
 * у источника, поэтому ровный частый опрос множил бы заказы, ничего не ускоряя. Растущая
 * пауза оставляет частыми первые секунды — те, за которые запись появляется чаще всего.
 */
const RECORDING_WAIT_MAX_INTERVAL_MS = 10_000;

/**
 * Код ответа, которым диалог без записи сообщает, что взять её неоткуда.
 * Прочие коды об отсутствии записи не говорят — это сбой запроса.
 */
const RECORDING_ABSENT_STATUS = 400;

/**
 * Чем закончился запрос записи диалога:
 * - ready — ссылка на запись получена;
 * - absent — записи нет и получить её неоткуда;
 * - failed — запись получить не удалось: ожидание не дало результата либо запрос не прошёл;
 * - cancelled — ожидание прекращено вызывающей стороной.
 */
export type RecordingUrlResult =
  | { status: 'ready'; audioUrl: string }
  | { status: 'absent' }
  | { status: 'failed' }
  | { status: 'cancelled' };

/**
 * Запрос адреса записи диалога по действию пользователя — с ожиданием записи, которую
 * подгружает интеграция-источник, и с возможностью это ожидание прекратить.
 *
 * Нужен там, где запись слушают из списка: у строки списка нет карточки диалога, в которой
 * ожидание жило бы само, поэтому ждёт сам запрос. Ответ без ссылки означает, что получение
 * записи заказано, — повторяем запрос, пока запись не появится или не выйдет срок ожидания.
 */
export const useConversationRecordingUrl = () => {
  const queryClient = useQueryClient();

  const requestRecordingUrl = useCallback(
    async (id: number | string, signal?: AbortSignal): Promise<RecordingUrlResult> => {
      const deadline = Date.now() + RECORDING_WAIT_TIMEOUT_MS;
      let intervalMs = RECORDING_WAIT_INITIAL_INTERVAL_MS;

      while (Date.now() < deadline) {
        if (signal?.aborted) return { status: 'cancelled' };

        let data: ConversationSignedUrlResponse;
        try {
          // staleTime: 0 — ответ об ожидании устаревает сразу, иначе опрос читал бы кэш
          data = await queryClient.fetchQuery({
            queryKey: conversationsKeys.signedUrl(id),
            queryFn: () => fetchConversationSignedUrl(id),
            staleTime: 0,
            retry: false,
          });
        } catch (error) {
          // Об отсутствии записи говорит только свой код ответа. Сетевой сбой, ошибка сервера
          // и истёкшая сессия записи не отменяют — выдавать их за «записи нет» нельзя
          return { status: getApiErrorStatus(error) === RECORDING_ABSENT_STATUS ? 'absent' : 'failed' };
        }

        if (signal?.aborted) return { status: 'cancelled' };
        if (data.url) return { status: 'ready', audioUrl: buildConversationAudioUrl(data.url) };
        // Ссылки нет и запись ниоткуда не ожидается — слушать нечего
        if (data.recordingState !== 'PENDING') return { status: 'absent' };

        // Пауза не выходит за срок ожидания: перебрать его — значит держать кнопку дольше обещанного
        const pauseMs = Math.min(intervalMs, deadline - Date.now());
        if (pauseMs <= 0) break;

        await delay(pauseMs, signal);
        intervalMs = Math.min(intervalMs * 2, RECORDING_WAIT_MAX_INTERVAL_MS);
      }

      return signal?.aborted ? { status: 'cancelled' } : { status: 'failed' };
    },
    [queryClient],
  );

  return { requestRecordingUrl };
};

/**
 * Хук для получения полного URL аудиофайла с подписанными параметрами для AudioPlayer.
 * При отсутствии аудио опрашивает GET /conversation/{id}/signed-url раз в секунду до 10 попыток,
 * ожидая появления аудиофайла (например, загруженного фоновым pipeline).
 * @param id - Conversation ID
 * @returns Audio URL with signed parameters or undefined if not ready
 */
export const useConversationAudioUrlWithSignedParams = (id?: string | number) => {
  const queryClient = useQueryClient();
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Основной запрос — без refetchInterval, без retry при ошибке
  const {
    data: signedUrlData,
    error,
    isError,
    isLoading,
  } = useQuery({
    queryKey: conversationsKeys.signedUrl(id!),
    queryFn: () => fetchConversationSignedUrl(id!),
    enabled: !!id,
    retry: false,
  });

  // Фоновый поллинг через setInterval + queryClient — не вызывает ре-рендеров.
  // Ждать нужно и по ответу без ссылки: запись, которую подгружает источник, приходит
  // теперь не отказом, а сообщением об ожидании, и по одному отказу ждать уже нельзя.
  // Отказ «взять запись неоткуда» ждать нечего — опрос по нему только тратил бы запросы.
  const isRecordingAwaited =
    (isError && getApiErrorStatus(error) !== RECORDING_ABSENT_STATUS) || (!!signedUrlData && !signedUrlData.url);

  useEffect(() => {
    if (!id) return;
    if (signedUrlData?.url) return;
    if (!isRecordingAwaited) return;

    let attempts = 0;
    pollRef.current = setInterval(async () => {
      attempts += 1;
      if (attempts >= AUDIO_POLL_MAX_ATTEMPTS) {
        if (pollRef.current) clearInterval(pollRef.current);
        return;
      }
      try {
        // staleTime: 0 — иначе опрос читал бы из кэша уже полученный ответ об ожидании
        const data = await queryClient.fetchQuery({
          queryKey: conversationsKeys.signedUrl(id!),
          queryFn: () => fetchConversationSignedUrl(id!),
          staleTime: 0,
        });
        if (data?.url && pollRef.current) {
          clearInterval(pollRef.current);
        }
      } catch {
        // Тихо продолжаем поллинг
      }
    }, AUDIO_POLL_INTERVAL_MS);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [id, isRecordingAwaited, signedUrlData?.url, queryClient]);

  const audioUrl = useMemo(() => {
    if (!signedUrlData?.url || !id) {
      return undefined;
    }

    return buildConversationAudioUrl(signedUrlData.url);
  }, [signedUrlData?.url, id]);

  return { audioUrl, isError: isError && !audioUrl, isLoading };
};

/**
 * Состояние отображения аудио-области карточки диалога:
 * - loading — признак состояния записи ещё неизвестен (карточка диалога грузится):
 *   держим стабильное зарезервированное место БЕЗ индикатора, чтобы не было дёрганья;
 * - unavailable — записи нет, показывать «запись недоступна»;
 * - pending — запись ожидается/подгружается, показывать индикатор загрузки;
 * - ready — ссылка на запись готова, показывать плеер.
 */
export type DialogAudioStatus = 'loading' | 'unavailable' | 'pending' | 'ready';

/**
 * Объединяет признак состояния записи из карточки диалога (recordingState) с фактической
 * готовностью подписанной ссылки на аудио. Нужен, чтобы аудио-область заранее отличала
 * «ждать запись» (индикатор) от «записи нет» (сразу «недоступна») и не дёргалась при
 * позднем появлении плеера.
 *
 * Индикатор (pending) показывается СТРОГО когда запись реально ожидается или уже готова, но
 * ссылка ещё догружается. Пока признак состояния неизвестен (карточка грузится) — состояние
 * loading: зарезервированное место без индикатора, чтобы не возникало скачка индикатор → «недоступна».
 */
export const useDialogAudioState = (id?: string | number) => {
  const { data: conversation } = useConversation(id);
  const { audioUrl, isError } = useConversationAudioUrlWithSignedParams(id);
  const recordingState = conversation?.recordingState;

  const status = useMemo<DialogAudioStatus>(() => {
    // Ссылка готова — показываем плеер независимо от признака состояния.
    if (audioUrl) return 'ready';
    // Запись ожидается (внешняя привязка + активный источник) либо уже готова, но ссылка ещё
    // догружается — индикатор в зарезервированном месте, плеер появится без скачка разметки.
    if (recordingState === 'PENDING' || recordingState === 'READY') return 'pending';
    // Записи нет вовсе — сразу «недоступна», без предварительного индикатора.
    if (recordingState === 'ABSENT') return 'unavailable';
    // Признак состояния ещё неизвестен (карточка грузится): держим зарезервированное место
    // без индикатора. Откат на «недоступна» — только по факту ошибки запроса ссылки
    // (legacy-контракт без признака состояния); индикатор «просто потому что ждём» не показываем.
    return isError ? 'unavailable' : 'loading';
  }, [audioUrl, recordingState, isError]);

  return { status, audioUrl };
};
